/**
 * Intake embed tokens — create and manage tokens that route embed form submissions to a queue.
 */

import { getSupabaseClient } from './supabaseClient';

export interface IntakeEmbedToken {
  id: string;
  workspaceId: string;
  queueId: string;
  token: string;
  label: string | null;
  defaultSource: string | null;
  createdAt: string;
}

const TABLE = 'intake_embed_tokens';

/** Generate a URL-safe token (12 chars) */
function generateToken(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  for (let i = 0; i < 12; i++) result += chars[bytes[i]! % chars.length];
  return result;
}

/**
 * List embed tokens for a workspace (optionally for a single queue).
 */
export async function listEmbedTokens(
  workspaceId: string,
  queueId?: string
): Promise<{ data: IntakeEmbedToken[] | null; error: string | null }> {
  const supabase = getSupabaseClient();
  let q = supabase
    .from(TABLE)
    .select('id, workspace_id, queue_id, token, label, default_source, created_at')
    .eq('workspace_id', workspaceId)
    .order('created_at', { ascending: false });
  if (queueId) q = q.eq('queue_id', queueId);
  const { data, error } = await q;
  if (error) return { data: null, error: error.message };
  const list = (data || []).map((row: Record<string, unknown>) => ({
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    queueId: row.queue_id as string,
    token: row.token as string,
    label: (row.label as string) || null,
    defaultSource: (row.default_source as string) || null,
    createdAt: row.created_at as string,
  }));
  return { data: list, error: null };
}

/**
 * Create an embed token for a queue. If one already exists for this workspace+queue, returns it.
 */
export async function createEmbedToken(
  workspaceId: string,
  queueId: string,
  label?: string,
  defaultSource?: string | null
): Promise<{ data: IntakeEmbedToken | null; error: string | null }> {
  const supabase = getSupabaseClient();
  const { data: existing } = await supabase
    .from(TABLE)
    .select('id, workspace_id, queue_id, token, label, default_source, created_at')
    .eq('workspace_id', workspaceId)
    .eq('queue_id', queueId)
    .maybeSingle();
  if (existing) {
    return {
      data: {
        id: existing.id,
        workspaceId: existing.workspace_id,
        queueId: existing.queue_id,
        token: existing.token,
        label: existing.label || null,
        defaultSource: existing.default_source || null,
        createdAt: existing.created_at,
      },
      error: null,
    };
  }
  const token = generateToken();
  const { data: inserted, error } = await supabase
    .from(TABLE)
    .insert({
      workspace_id: workspaceId,
      queue_id: queueId,
      token,
      label: label || null,
      default_source: defaultSource || null,
    })
    .select('id, workspace_id, queue_id, token, label, default_source, created_at')
    .single();
  if (error) return { data: null, error: error.message };
  return {
    data: {
      id: inserted.id,
      workspaceId: inserted.workspace_id,
      queueId: inserted.queue_id,
      token: inserted.token,
      label: inserted.label || null,
      defaultSource: inserted.default_source || null,
      createdAt: inserted.created_at,
    },
    error: null,
  };
}

/**
 * Update the default_source on an existing embed token.
 */
export async function updateEmbedTokenSource(
  id: string,
  defaultSource: string | null
): Promise<{ error: string | null }> {
  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from(TABLE)
    .update({ default_source: defaultSource })
    .eq('id', id);
  return { error: error?.message ?? null };
}

/**
 * Delete an embed token by id.
 */
export async function deleteEmbedToken(id: string): Promise<{ error: string | null }> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.from(TABLE).delete().eq('id', id);
  return { error: error?.message ?? null };
}
